import React, { useEffect, useRef } from 'react';
import SingleSelect from 'components/SingleSelect';
import MultiSelect from 'components/MultiSelect';
import SearchSelect from 'components/SearchSelect';
import DateRangerPicker from 'components/DateRangePicker';
import InputFilterItem from 'components/InputFilterItem';
import { useTmsSearch } from '../../hooks/useTmsSearch';
import { useFilteredCarriers, isCarrierValidForRouteType } from '../../hooks/useFilteredCarriers';
import { TmsCarrierType } from '../../schedule/graphql/queries/getTmsCarriers';
import { WaypointTypeEnum } from '../../../../enums/TmsEnum';
import { t } from '../../../../i18n';
import { getSmartLabelClassName } from '../../../../utils/labelWidthUtils';

export interface FilterOptions {
    taskType: string;
    businessType: string;
    routeId: string;
    routeCode: string;
    taskNumber: string;
    taskStatus: string[];
    routeType: string;
    scheduleId: string;
    carrierId: string;
    driverId: string;
    vehicleId: string;
    // - undefined：未初始化（UI 默认展示最近一周）
    // - null：用户手动清空（点击叉号），保持为空
    // - [start, end]：用户选择的时间范围
    taskDate?: [string | null, string | null] | null;
}

// 任务状态枚举值：1-草稿 2-已发布 3-进行中 5-异常（默认选中这四项）
const DEFAULT_TASK_STATUS_VALUES = ['2', '3', '5'];

export const DEFAULT_FILTER_OPTIONS: FilterOptions = {
    taskType: '',
    businessType: '',
    routeId: '',
    routeCode: '',
    taskNumber: '',
    taskStatus: DEFAULT_TASK_STATUS_VALUES,
    routeType: '',
    scheduleId: '',
    carrierId: '',
    driverId: '',
    vehicleId: '',
    taskDate: undefined
};

interface ShipToHereTaskFilterProps {
    taskOptions: { label: string; value: string | number }[];
    statusOptions: { label: string; value: string | number }[];
    routeTypeOptions: { label: string; value: string | number }[];
    businessTypeOptions: { label: string; value: string | number }[];
    carriersData: TmsCarrierType[] | undefined;
    driverOptions: { label: string; value: string }[];
    vehicleOptions: { label: string; value: string | number }[];
    initialFilters?: FilterOptions;
    currentRouteType?: string; // 当前 tab 选中的 routeType
    filters: FilterOptions;
    setFilters: React.Dispatch<React.SetStateAction<FilterOptions>>;
    resetSignal?: number;
}

const ShipToHereTaskFilter: React.FC<ShipToHereTaskFilterProps> = ({ taskOptions, statusOptions, routeTypeOptions, businessTypeOptions, carriersData, driverOptions, vehicleOptions, initialFilters, currentRouteType, filters: controlledFilters, setFilters, resetSignal }) => {
    const { handleRouteSearch, handleScheduleSearch } = useTmsSearch();
    const hasUserEditedTaskDateRef = useRef(false);
    const filters = controlledFilters ?? initialFilters ?? DEFAULT_FILTER_OPTIONS;

    useEffect(() => {
        // 重置时恢复为“未手动编辑 taskDate”的状态
        hasUserEditedTaskDateRef.current = false;
    }, [resetSignal]);

    // 当 initialFilters 变化时，更新内部状态（特别是 taskDate）
    useEffect(() => {
        if (initialFilters?.taskDate) {
            hasUserEditedTaskDateRef.current = true;
            // 如果 initialFilters 的 taskDate 与当前 filters 的 taskDate 不同，则更新
            const currentTaskDateStr = filters.taskDate ? JSON.stringify(filters.taskDate) : null;
            const newTaskDateStr = JSON.stringify(initialFilters.taskDate);
            if (currentTaskDateStr !== newTaskDateStr) {
                setFilters(prev => ({
                    ...prev,
                    taskDate: initialFilters.taskDate
                }));
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [initialFilters?.taskDate]);

    // 同步 currentRouteType 到本地 filters.routeType
    useEffect(() => {
        if (currentRouteType !== undefined) {
            // 只有当 currentRouteType 与当前值不同时才更新
            if (filters.routeType !== currentRouteType) {
                // 切换 tab 时清空路线选项、班次选项和承运商选项
                setFilters(prev => ({ ...prev, routeType: currentRouteType, routeId: '', scheduleId: '', carrierId: '' }));
            }
        }
    }, [currentRouteType, filters.routeType, setFilters]);

    // Filter carriers based on routeType using the reusable hook
    // 优先使用 currentRouteType，如果没有则使用 filters.routeType
    const effectiveRouteType = currentRouteType || filters.routeType;
    const carrierOptions = useFilteredCarriers(carriersData, effectiveRouteType);

    void routeTypeOptions;

    const handleFilterChange = (name: string, value: any) => {
        let processedValue = value;
        // 对于 taskStatus，确保是数组类型
        if (name === 'taskStatus') {
            processedValue = Array.isArray(value) ? value : [];
        } else if (typeof value === 'string' && value.trim() === '') {
            processedValue = undefined;
        }

        if (name === 'taskDate') {
            hasUserEditedTaskDateRef.current = true;
        }
        
        // Special handling for routeType change: check if current carrierId is still valid
        if (name === 'routeType' && value !== undefined && carriersData && filters.carrierId) {
            const isValid = isCarrierValidForRouteType(carriersData, filters.carrierId, value);
            if (!isValid) {
                // Clear carrierId if it's no longer valid
                setFilters(prev => {
                    const shouldClearDefaultTaskDate = prev.taskDate === undefined && !hasUserEditedTaskDateRef.current;
                    return {
                        ...prev,
                        [name]: processedValue,
                        carrierId: '',
                        ...(shouldClearDefaultTaskDate ? { taskDate: null } : null),
                    };
                });
                return;
            }
        }
        
        setFilters(prev => {
            const shouldClearDefaultTaskDate = name !== 'taskDate' && prev.taskDate === undefined && !hasUserEditedTaskDateRef.current;
            return {
                ...prev,
                [name]: processedValue,
                ...(shouldClearDefaultTaskDate ? { taskDate: null } : null),
            };
        });
    };

    const labelTaskDate = String(t('tms.task.filters.taskDate'));
    const labelTaskType = String(t('tms.task.filters.taskType'));
    const labelBusinessType = String(t('tms.task.filters.businessType'));
    const labelRoute = String(t('tms.task.filters.route'));
    const labelRouteCode = String(t('tms.task.filters.routeCode'));
    const labelTaskStatus = String(t('tms.task.filters.taskStatus'));
    const labelSchedule = String(t('tms.task.filters.schedule'));
    const labelCarrier = String(t('tms.task.filters.carrier'));
    const labelDriver = String(t('tms.task.filters.driver'));
    const labelVehicle = String(t('tms.task.filters.vehicle'));
    const labelTaskNumber = String(t('tms.task.filters.taskNumber'));

    const column1Labels = [labelTaskDate, labelTaskNumber, labelRoute, labelDriver];
    const column2Labels = [labelTaskType, labelTaskStatus, labelSchedule, labelVehicle];
    const column3Labels = [labelBusinessType, labelRouteCode, labelCarrier];

    return (
        <div className="mb-2 pl-0 py-2">
            <div className="grid grid-cols-3 gap-x-10 gap-y-4 items-start">
                <div className="min-w-0">
                    <DateRangerPicker
                        label={labelTaskDate}
                        value={filters.taskDate}
                        onChange={(dates) => handleFilterChange('taskDate', dates)}
                        spanClassName={getSmartLabelClassName(column1Labels)}
                    />
                </div>
                <div className="min-w-0">
                    <SingleSelect
                        label={labelTaskType}
                        value={filters.taskType}
                        onChange={value => handleFilterChange('taskType', value)}
                        options={taskOptions}
                        spanClassName={getSmartLabelClassName(column2Labels)}
                    />
                </div>
                <div className="min-w-0">
                    <SingleSelect
                        label={labelBusinessType}
                        value={filters.businessType}
                        onChange={value => handleFilterChange('businessType', value)}
                        options={businessTypeOptions}
                        spanClassName={getSmartLabelClassName(column3Labels)}
                    />
                </div>
                <div className="min-w-0">
                    <InputFilterItem
                        label={labelTaskNumber}
                        value={filters.taskNumber}
                        onChange={(e) => handleFilterChange('taskNumber', e.target.value)}
                        placeholder={String(t('tms.task.filters.search'))}
                        spanClassName={getSmartLabelClassName(column1Labels)}
                    />
                </div>
                <div className="min-w-0">
                    <MultiSelect
                        label={labelTaskStatus}
                        value={filters.taskStatus}
                        onChange={value => handleFilterChange('taskStatus', value)}
                        options={statusOptions}
                        spanClassName={getSmartLabelClassName(column2Labels)}
                    />
                </div>
                <div className="min-w-0">
                    <InputFilterItem
                        label={labelRouteCode}
                        value={filters.routeCode}
                        onChange={(e) => handleFilterChange('routeCode', e.target.value)}
                        placeholder={String(t('tms.task.filters.search'))}
                        spanClassName={getSmartLabelClassName(column3Labels)}
                    />
                </div>
                <div className="min-w-0">
                    <SearchSelect
                        label={labelRoute}
                        placeholder={String(t('tms.task.filters.search'))}
                        value={filters.routeId}
                        onChange={(value) => {
                            handleFilterChange('routeId', value);
                        }}
                        onSearch={(searchValue, callback) => {
                            const routeType = effectiveRouteType ? Number(effectiveRouteType) : undefined;
                            handleRouteSearch(searchValue, callback, undefined, false, routeType, [WaypointTypeEnum.Waypoint, WaypointTypeEnum.Destination]);
                        }}
                        spanClassName={getSmartLabelClassName(column1Labels)}
                    />
                </div>
                <div className="min-w-0">
                    <SearchSelect
                        label={labelSchedule}
                        placeholder={String(t('tms.task.filters.search'))}
                        value={filters.scheduleId}
                        onChange={(value) => {
                            handleFilterChange('scheduleId', value);
                        }}
                        onSearch={handleScheduleSearch}
                        spanClassName={getSmartLabelClassName(column2Labels)}
                    />
                </div>
                <div className="min-w-0">
                    <SingleSelect
                        label={labelCarrier}
                        value={filters.carrierId}
                        onChange={value => handleFilterChange('carrierId', value)}
                        options={carrierOptions}
                        spanClassName={getSmartLabelClassName(column3Labels)}
                    />
                </div>
                <div className="min-w-0">
                    <SingleSelect
                        label={labelDriver}
                        value={filters.driverId}
                        onChange={value => handleFilterChange('driverId', value)}
                        options={driverOptions}
                        spanClassName={getSmartLabelClassName(column1Labels)}
                    />
                </div>
                <div className="min-w-0">
                    <SingleSelect
                        label={labelVehicle}
                        value={filters.vehicleId}
                        onChange={value => handleFilterChange('vehicleId', value)}
                        options={vehicleOptions}
                        spanClassName={getSmartLabelClassName(column2Labels)}
                    />
                </div>
                <div className="min-w-0" />
            </div>
        </div>
    );
};

export default ShipToHereTaskFilter;
